import React from "react";
import { Button, ClassNameProps, Column, Heading, Modal, Row, StyleProps } from "@hex-insights/core";
import { useActivePageRegistration } from "@hex-insights/router";
import { Icon, IconContainer } from "../../../../../../Components/Icon";
import { Tile } from "../../../../../../Components/Tile";
import { Timeline } from "../../../../../../Components/Timeline";
import {
	OrderDirection,
	StudentDetailQuery,
	StudentMedicalConditionOrderField,
	StudentNoteNoteType,
	StudentNoteOrderField,
	StudentOTCMedicationOrderField,
	StudentVaccinationRecordOrderField,
	TimelineFilterInput,
	TimelineObjectType,
	useStudentMedicalConditionStudentListQuery,
	useStudentMedicalContactStudentListQuery,
	useStudentNoteStudentPinnedListQuery,
	useStudentOTCMedicationStudentListQuery,
	useStudentPrescriptionMedicationStudentListQuery,
	useStudentVaccinationRecordStudentListQuery,
} from "../../../../../../Utilities";
import {
	StudentMedicalConditionStudentListModal,
	StudentMedicalConditionStudentListSection,
} from "../../../../StudentMedicalCondition";
import { StudentMedicalContactStudentListSection } from "../../../../StudentMedicalContact/StudentList";
import { StudentMedicalContactStudentListModal } from "../../../../StudentMedicalContact/StudentListModal";
import { StudentMedicalEventCreateModalController } from "../../../../StudentMedicalEvent";
import { PinnedStudentNotesRow } from "../../../../StudentNote";
import { StudentOTCMedicationStudentOverviewCard } from "../../../../StudentOTCMedication";
import {
	StudentPrescriptionMedicationStudentListModal,
	StudentPrescriptionMedicationStudentListSection,
} from "../../../../StudentPrescriptionMedication";
import { StudentVaccinationRecordStudentOverviewCard } from "../../../../StudentVaccinationRecord";
import { DetailRouteContext } from "../../RouteContext";

export type HealthBodyProps = {
	student: StudentDetailQuery["student"];
};

export function HealthBody({ student }: HealthBodyProps) {
	const { subPageInfos } = React.useContext(DetailRouteContext);
	useActivePageRegistration(subPageInfos.health);

	return (
		<Row justify="spaced-start" style={{ flexGrow: 1 }}>
			<Column justify="spaced-start" style={{ width: "25rem", flexShrink: 0 }}>
				<MedicalConditionsSection student={student} />
				<PrescriptionMedicationsSection student={student} />
				<MedicalContactsSection student={student} />
			</Column>
			<Column justify="spaced-start" style={{ flexGrow: 1, minWidth: 0 }}>
				<Row justify="spaced-start" align="center">
					<PinnedNotes student={student} style={{ flexGrow: 1, minWidth: 0 }} />
					<MedicalEventsCreate student={student} />
				</Row>
				<Row justify="spaced-start">
					<OTCMedicationsSection student={student} style={{ width: "50%" }} />
					<VaccinationsSection student={student} style={{ width: "50%" }} />
				</Row>
				<TimelineTile student={student} />
			</Column>
		</Row>
	);
}

type PageSectionProps = {
	student: StudentDetailQuery["student"];
} & Partial<ClassNameProps & StyleProps>;

function MedicalConditionsSection({ student, className, style }: PageSectionProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { loading, data } = useStudentMedicalConditionStudentListQuery({
		variables: {
			filters: { studentIDEQ: student.id },
			order: { field: StudentMedicalConditionOrderField.IsProminent, direction: OrderDirection.Desc },
		},
	});
	const studentMedicalConditions = React.useMemo(
		() => data?.studentMedicalConditionConnection.edges.map((e) => e.node),
		[data],
	);

	return (
		<React.Fragment>
			<Tile className={className} style={style}>
				<Tile.Header>
					<Row justify="space-between" align="center">
						<Row justify="spaced-start" align="center">
							<IconContainer color="var(--theme-colors__pink--background)">
								<Icon.Asterisk color="var(--theme-colors__pink)" size={18} />
							</IconContainer>

							<Heading.H3 noMargin>Allergies and Conditions</Heading.H3>
						</Row>

						<Button variant="link" size="small" onClick={openModal} className="button--icon">
							<Icon.Gear size={16} className="block" />
						</Button>
					</Row>
				</Tile.Header>
				<Tile.Body>
					<StudentMedicalConditionStudentListSection
						isLoading={loading}
						student={student}
						studentMedicalConditions={studentMedicalConditions}
					/>
				</Tile.Body>
			</Tile>

			<Modal.If condition={isModalOpen}>
				<StudentMedicalConditionStudentListModal
					isLoading={loading}
					student={student}
					studentMedicalConditions={studentMedicalConditions}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

function PrescriptionMedicationsSection({ student, className, style }: PageSectionProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { loading, data } = useStudentPrescriptionMedicationStudentListQuery({
		variables: {
			filters: { studentIDEQ: student.id },
		},
	});
	const studentPrescriptionMedications = React.useMemo(
		() => data?.studentPrescriptionMedicationConnection.edges.map((e) => e.node),
		[data],
	);

	return (
		<React.Fragment>
			<Tile className={className} style={style}>
				<Tile.Header>
					<Row justify="space-between" align="center">
						<Row justify="spaced-start" align="center">
							<IconContainer color="var(--theme-colors__purple--background)">
								<Icon.Prescription color="var(--theme-colors__purple)" size={18} />
							</IconContainer>

							<Heading.H3 noMargin>Prescription Medications</Heading.H3>
						</Row>

						<Button variant="link" size="small" onClick={openModal} className="button--icon">
							<Icon.Gear size={16} className="block" />
						</Button>
					</Row>
				</Tile.Header>
				<Tile.Body>
					<StudentPrescriptionMedicationStudentListSection
						isLoading={loading}
						student={student}
						studentPrescriptionMedications={studentPrescriptionMedications}
					/>
				</Tile.Body>
			</Tile>

			<Modal.If condition={isModalOpen}>
				<StudentPrescriptionMedicationStudentListModal
					isLoading={loading}
					student={student}
					studentPrescriptionMedications={studentPrescriptionMedications}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

function MedicalContactsSection({ student, className, style }: PageSectionProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { loading, data } = useStudentMedicalContactStudentListQuery({
		variables: {
			filters: {
				studentIDEQ: student.id,
			},
		},
	});
	const studentMedicalContacts = React.useMemo(
		() => data?.studentMedicalContactConnection.edges.map((e) => e.node),
		[data],
	);

	return (
		<React.Fragment>
			<Tile className={className} style={style}>
				<Tile.Header>
					<Row justify="space-between" align="center">
						<Row justify="spaced-start" align="center">
							<IconContainer color="var(--theme-colors__blue--background)">
								<Icon.AddressBook color="var(--theme-colors__blue)" size={18} />
							</IconContainer>

							<Heading.H3 noMargin>Medical Contacts</Heading.H3>
						</Row>

						<Button variant="link" size="small" onClick={openModal} className="button--icon">
							<Icon.Gear size={16} className="block" />
						</Button>
					</Row>
				</Tile.Header>
				<Tile.Body>
					<StudentMedicalContactStudentListSection
						isLoading={loading}
						student={student}
						studentMedicalContacts={studentMedicalContacts}
					/>
				</Tile.Body>
			</Tile>

			<Modal.If condition={isModalOpen}>
				<StudentMedicalContactStudentListModal
					isLoading={loading}
					student={student}
					studentMedicalContacts={studentMedicalContacts}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

function MedicalEventsCreate({ student, className, style }: PageSectionProps) {
	const formStateArgs = React.useMemo(
		() => ({
			initialFormValues: { studentID: student.id },
		}),
		[student.id],
	);

	return (
		<StudentMedicalEventCreateModalController
			variant="primary"
			formStateArgs={formStateArgs}
			formNameSuffix={student.id}
			className={className}
			style={style}
		>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
				<Icon.FirstAid size={16} /> Record Medical Event
			</Row>
		</StudentMedicalEventCreateModalController>
	);
}

function OTCMedicationsSection({ student, className, style }: PageSectionProps) {
	const { loading, data } = useStudentOTCMedicationStudentListQuery({
		variables: {
			filters: {
				studentIDEQ: student.id,
			},
			order: { field: StudentOTCMedicationOrderField.IsAllowed, direction: OrderDirection.Asc },
		},
	});
	const studentOTCMedications = React.useMemo(
		() => data?.studentOTCMedicationConnection.edges.map((e) => e.node),
		[data],
	);

	return (
		<StudentOTCMedicationStudentOverviewCard
			isLoading={loading}
			student={student}
			studentOTCMedications={studentOTCMedications}
			className={className}
			style={style}
		/>
	);
}

function VaccinationsSection({ student, className, style }: PageSectionProps) {
	const { loading, data } = useStudentVaccinationRecordStudentListQuery({
		variables: {
			filters: {
				studentIDEQ: student.id,
			},
			order: {
				field: StudentVaccinationRecordOrderField.ReceivedOn,
				direction: OrderDirection.Desc,
			},
		},
	});
	const studentVaccinationRecords = React.useMemo(
		() => data?.studentVaccinationRecordConnection.edges.map((e) => e.node),
		[data],
	);

	return (
		<StudentVaccinationRecordStudentOverviewCard
			isLoading={loading}
			student={student}
			studentVaccinationRecords={studentVaccinationRecords}
			className={className}
			style={style}
		/>
	);
}

function PinnedNotes({ student, className, style }: PageSectionProps) {
	const { loading, data } = useStudentNoteStudentPinnedListQuery({
		variables: {
			filters: {
				primaryStudents: [{ idEQ: student.id }],
				isPinnedEQ: true,
				noteTypeIn: [StudentNoteNoteType.Health, StudentNoteNoteType.Injury, StudentNoteNoteType.Medication],
			},
			order: { field: StudentNoteOrderField.CreatedAt, direction: OrderDirection.Desc },
		},
	});
	const studentNotes = React.useMemo(() => data?.studentNoteConnection.edges.map((e) => e.node), [data]);

	const studentNoteFormStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				primaryStudentIDs: [student.id],
			},
		}),
		[student.id],
	);

	return (
		<PinnedStudentNotesRow
			isLoading={loading}
			studentNotes={studentNotes}
			formStateArgs={studentNoteFormStateArgs}
			formNameSuffix={student.id}
			className={className}
			style={style}
		/>
	);
}

function TimelineTile({ student, className, style }: PageSectionProps) {
	const filter = React.useMemo<TimelineFilterInput>(
		() => ({
			objectTypeIn: [
				TimelineObjectType.StudentMedicalEvent,
				TimelineObjectType.StudentNote,
				TimelineObjectType.StudentHealthNote,
				TimelineObjectType.StudentInjuryNote,
				TimelineObjectType.StudentMedicationNote,
			],
			studentID: student.id,
		}),
		[student.id],
	);

	return (
		<Tile className={className} style={{ flexGrow: 1, ...style }}>
			<Tile.Body>
				<Timeline filter={filter} pageSize={10} style={{ height: "100%" }} />
			</Tile.Body>
		</Tile>
	);
}
